import type { Timestamp } from 'firebase/firestore';

export interface LocationOverride {
  latitude: number;
  longitude: number;
  label?: string;
}

export interface UserSettings {
  notificationsEnabled: boolean;
  locationAllowed: boolean;
  analyticsConsent?: boolean;
  /** Admin-only discovery position override; never affects the public profile location. */
  locationOverride?: LocationOverride | null;
}

/** Per-account tutorial/explainer flags at users/{uid}/settings/tutorials. */
export interface UserTutorials {
  /** Set when the "Pulp me today" explainer has been shown. */
  liveButtonSeenAt?: string;
}

export interface SubscriptionStatus {
  active: boolean;
  cancelled?: boolean;
  pending?: boolean;
  expiresAt?: string | null;
  provider?: 'revenuecat' | 'stripe' | null;
  productId?: string | null;
  priceId?: string | null;
  customerId?: string | null;
  subscriptionId?: string | null;
  updatedAt?: string;
}

export interface ProfilePhoto {
  id: string;
  url: string;
  /** Storage object path, when the photo lives in Firebase Storage. */
  path?: string;
  /** Index of the photo within the profile's photo list. */
  position?: number;
  createdAt?: string;
}

export interface UserProfile {
  uid: string;
  name: string;
  email: string;
  onboarded: boolean;
  visible?: boolean;
  birthday?: string; // YYYY-MM-DD
  zodiac?: string;
  age?: number;
  gender?: string[];
  orientation?: string[];
  pronouns?: string;
  city?: string;
  country?: string;
  languages?: string[];
  ethnicity?: string | string[];
  height?: number; // cm
  interests?: string[];
  spots?: string[];
  openTo?: string[];
  relationshipTypes?: string[];
  relationshipStatus?: string;
  intentions?: string[];
  availableToday?: boolean;
  takingABreak?: boolean;
  intimacy?: string[];
  bio?: string;
  photos?: ProfilePhoto[];
  latitude?: number;
  longitude?: number;
  geohash?: string;
  onboardingStep?: number;
  createdAt: string;
  onboardedAt?: string;
  lastLoggedInAt?: string;
  lastActiveAt?: string;
  lastActive?: string;
  inviteCodeUsed?: string;
  inviteCodeUsedAt?: string; // ISO timestamp when the invite code was claimed
  welcomeEmailSentAt?: string; // ISO timestamp when the welcome email was sent
  termsAcceptedAt?: string; // ISO timestamp when user accepted terms (GDPR)
  pwaInstalledAt?: string[]; // ISO timestamps of PWA installs (one per install)
  fids?: string[]; // Firebase Installation IDs for FCM (one per device)
  status?: 'active' | 'inactive' | 'banned' | 'deleted';
  bannedAt?: string; // ISO timestamp when the user was banned
  deletedAt?: string; // ISO timestamp when the user was soft-deleted
  subscription?: SubscriptionStatus;
}

export interface Venue {
  id: string;
  name: string;
  description?: string;
  city: string;
  country: string;
  latitude?: number;
  longitude?: number;
  category: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SwipeLike {
  id?: string;
  fromUid: string;
  toUid: string;
  createdAt: string;
}

export interface Match {
  id?: string;
  userUids: string[]; // [uid1, uid2]
  createdAt: string;
}

export interface ChatMessage {
  id?: string;
  matchId: string;
  senderUid: string;
  text: string;
  createdAt: string;
}

export interface Note {
  uid: string;
  text: string;
  createdAt: string;
  expiresAt: Timestamp;
  latitude?: number;
  longitude?: number;
  /** Stamped by the stampNoteAuthor trigger; absent until it lands. */
  authorName?: string;
  authorPhotoUrl?: string;
  availableToday?: boolean;
  visible?: boolean;
}

export const GENDER_OPTIONS = [
  'Woman',
  'Man',
  'Non-binary',
  'Genderqueer',
  'Agender',
  'Transgender Woman',
  'Transgender Man',
  'Prefer not to say',
];

export const ORIENTATION_OPTIONS = [
  'Straight',
  'Gay',
  'Lesbian',
  'Bisexual',
  'Asexual',
  'Pansexual',
  'Queer',
  'Demisexual',
];

export const PRONOUN_OPTIONS = [
  'she/her',
  'he/him',
  'they/them',
  'she/they',
  'he/they',
  'any pronouns',
  'other',
];

export const LANGUAGE_OPTIONS = [
  'English',
  'French',
  'Spanish',
  'German',
  'Italian',
  'Portuguese',
  'Chinese',
  'Japanese',
  'Arabic',
  'Russian',
  'Hindi',
  'Dutch',
  'Swedish',
];

export const ETHNICITY_OPTIONS = [
  'Asian',
  'Black / African descent',
  'Hispanic / Latino',
  'Middle Eastern',
  'White / Caucasian',
  'Mixed Heritage',
  'Indigenous / Native',
  'Other',
];

export const INTEREST_OPTIONS = [
  'Music',
  'Art & Design',
  'Hiking & Outdoors',
  'Cooking & Baking',
  'Photography',
  'Travel & Adventure',
  'Reading & Lit',
  'Tech & Coding',
  'Board Games',
  'Running & Athletics',
  'Yoga & Pilates',
  'Cinema & Movies',
  'Dancing',
  'Veganism & Vegetarian',
  'Coffee Culture',
  'Wine & Dine',
  'Craft Beer',
  'Astrology & Tarot',
  'Gardening & Plants',
  'Live Music / Concerts',
];

export interface InviteCode {
  code: string;
  maxClaims: number;
  claimCount: number;
  createdBy: string;
  expireDate: string | null;
}

export function calculateAge(birthdayString: string | undefined): number {
  if (!birthdayString) return 24;
  const birth = new Date(birthdayString);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export function formatLastActive(lastLoggedInAt: string | undefined): string {
  if (!lastLoggedInAt) return '';

  const then = new Date(lastLoggedInAt);
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 0) return '';

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks}w ago`;
  }

  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
